import moment from "moment";

export class ClientFiche {

  static deserialize(dto: ClientFicheDto): ClientFiche {
    return new ClientFiche(
      (dto.andereDiensten || []).map(clientFicheDienstDto => ClientFicheDienst.deserialize(clientFicheDienstDto)),
      new Date(dto.laatstGewijzigdeTijdstip),
      dto.algemeneInformatie ? AlgemeneInformatie.deserialize(dto.algemeneInformatie) : undefined,
      dto.takenpakket ? Takenpakket.deserialize(dto.takenpakket) : undefined,
      dto.knelpunten ? Knelpunten.deserialize(dto.knelpunten) : undefined,
      dto.mogelijkeProblematieken ? MogelijkeProblematieken.deserialize(dto.mogelijkeProblematieken) : undefined,
    )
  }

  constructor(
    private andereDiensten: Array<ClientFicheDienst>,
    private laatstGewijzigdeTijdstip: Date,
    private algemeneInformatie?: AlgemeneInformatie,
    private takenpakket?: Takenpakket,
    private knelpunten?: Knelpunten,
    private mogelijkeProblematieken?: MogelijkeProblematieken,
  ) {
    this.algemeneInformatie = algemeneInformatie?.hasContent() ? algemeneInformatie : undefined;
    this.takenpakket = takenpakket?.hasContent() ? takenpakket : undefined;
    this.knelpunten = knelpunten?.hasContent() ? knelpunten : undefined;
    this.mogelijkeProblematieken = mogelijkeProblematieken?.hasContent() ? mogelijkeProblematieken : undefined;
  }

  getAndereDiensten(): Array<ClientFicheDienst> {
    return this.andereDiensten;
  }

  hasAndereDiensten(): boolean {
    return this.andereDiensten.length > 0;
  }

  getLaatstGewijzigdeTijdstip(): Date {
    return this.laatstGewijzigdeTijdstip;
  }

  getAlgemeneInformatie(): AlgemeneInformatie | undefined {
    return this.algemeneInformatie;
  }

  getTakenpakket(): Takenpakket | undefined {
    return this.takenpakket;
  }

  getKnelpunten(): Knelpunten | undefined {
    return this.knelpunten;
  }

  getMogelijkeProblematieken(): MogelijkeProblematieken | undefined {
    return this.mogelijkeProblematieken;
  }
}

export class ClientFicheDienst {
  static deserialize(dto: ClientFicheDienstDto) {
    return new ClientFicheDienst(
      dto.functie,
      dto.naamDienstverlenerOfOrganisatie,
      dto.extraInfo,
    );
  }

  constructor(
    private functie?: string,
    private naamDienstverlenerOfOrganisatie?: string,
    private extraInfo?: string,
  ) {
  }

  getFunctie(): string | undefined {
    return this.functie;
  }

  getNaamDienstverlenerOfOrganisatie(): string | undefined {
    return this.naamDienstverlenerOfOrganisatie;
  }

  getExtraInfo(): string | undefined {
    return this.extraInfo;
  }

  hasContent(): boolean {
    return !!this.functie
      || !!this.naamDienstverlenerOfOrganisatie
      || !!this.extraInfo
  }

}

export class AlgemeneInformatie {
  static deserialize(dto: AlgemeneInformatieDto) {
    return new AlgemeneInformatie(
      dto.dienstencheques,
      dto.gebruikersnummer,
      dto.geboortedatum,
      dto.moedertaal,
      dto.conversatieTaal,
      dto.keyLock,
      dto.autoStaanPlaats,
      dto.extraInfoBereikbaarheid,
      dto.inwonendeGezinsleden,
      dto.socialeSituatieSchets,
      dto.extraInformatie,
    );
  }

  constructor(
    private dienstencheques?: DienstenChequesType,
    private gebruikersnummer?: number,
    private geboortedatum?: string,
    private moedertaal?: string,
    private conversatieTaal?: string,
    private keyLock?: string,
    private autoStaanPlaats?: string,
    private extraInfoBereikbaarheid?: string,
    private inwonendeGezinsleden?: string,
    private socialeSituatieSchets?: string,
    private extraInformatie?: string,
  ) {
  }

  getDienstencheques(): DienstenChequesType | undefined {
    return this.dienstencheques;
  }

  getGebruikersnummer(): number | undefined {
    return this.gebruikersnummer;
  }

  getGeboortedatum(): string | undefined {
    return this.geboortedatum;
  }

  getGeboortedatumAsDate(): Date | undefined {
    return this.geboortedatum ? moment(this.geboortedatum, "DD-MM-YYYY").toDate() : undefined;
  }

  getMoedertaal(): string | undefined {
    return this.moedertaal;
  }

  getConversatieTaal(): string | undefined {
    return this.conversatieTaal;
  }

  getKeyLock(): string | undefined {
    return this.keyLock;
  }

  getAutoStaanPlaats(): string | undefined {
    return this.autoStaanPlaats;
  }

  getExtraInfoBereikbaarheid(): string | undefined {
    return this.extraInfoBereikbaarheid;
  }

  getInwonendeGezinsleden(): string | undefined {
    return this.inwonendeGezinsleden;
  }

  getSocialeSituatieSchets(): string | undefined {
    return this.socialeSituatieSchets;
  }

  getExtraInformatie(): string | undefined {
    return this.extraInformatie;
  }

  hasContent(): boolean {
    return !!this.dienstencheques
      || !!this.gebruikersnummer
      || !!this.geboortedatum
      || !!this.moedertaal
      || !!this.conversatieTaal
      || !!this.keyLock
      || !!this.autoStaanPlaats
      || !!this.extraInfoBereikbaarheid
      || !!this.inwonendeGezinsleden
      || !!this.socialeSituatieSchets
      || !!this.extraInformatie
  }
}

export enum DienstenChequesType {
  ELEKTRONISCH = 'Elektronisch',
  PAPIER = 'Papier',
}

export class Takenpakket {

  static deserialize(dto: TakenpakketDto): Takenpakket {
    return new Takenpakket(
      dto.redenOpstart,
      dto.teBehalenDoelstellingen,
      dto.poetsdienst ? TakenpakketCategorie.deserialize(dto.poetsdienst) : undefined,
      dto.gezinszorg ? TakenpakketCategorie.deserialize(dto.gezinszorg) : undefined,
      dto.maaltijdenAanHuis ? TakenpakketCategorie.deserialize(dto.maaltijdenAanHuis) : undefined,
      dto.klusjesdienst ? TakenpakketCategorie.deserialize(dto.klusjesdienst) : undefined,
      dto.psycholoog ? TakenpakketCategorie.deserialize(dto.psycholoog) : undefined,
      dto.ergotherapeut ? TakenpakketCategorie.deserialize(dto.ergotherapeut) : undefined,
      dto.mantelzorgcoordinator ? TakenpakketCategorie.deserialize(dto.mantelzorgcoordinator) : undefined,
      dto.casemanager ? TakenpakketCategorie.deserialize(dto.casemanager) : undefined,
      dto.casaClementina ? CasaClementina.deserialize(dto.casaClementina) : undefined,
      dto.stofzuigerWaarTeVinden,
      dto.poetsproductenWaarTeVinden,
      dto.doekenVoddenDweilenWaarTeVinden,
      dto.telescopischeSteelWaarTeVinden,
      dto.andereWaarTeVinden,
      dto.aandachtspuntenPoetsen,
      dto.aandachtspuntenAndereTaken,
    );
  }

  constructor(
    private redenOpstart?: string,
    private teBehalenDoelstellingen?: string,
    private poetsdienst?: TakenpakketCategorie,
    private gezinszorg?: TakenpakketCategorie,
    private maaltijdenAanHuis?: TakenpakketCategorie,
    private klusjesdienst?: TakenpakketCategorie,
    private psycholoog?: TakenpakketCategorie,
    private ergotherapeut?: TakenpakketCategorie,
    private mantelzorgcoordinator?: TakenpakketCategorie,
    private casemanager?: TakenpakketCategorie,
    private casaClementina?: CasaClementina,
    private stofzuigerWaarTeVinden?: string,
    private poetsproductenWaarTeVinden?: string,
    private doekenVoddenDweilenWaarTeVinden?: string,
    private telescopischeSteelWaarTeVinden?: string,
    private andereWaarTeVinden?: string,
    private aandachtspuntenPoetsen?: string,
    private aandachtspuntenAndereTaken?: string,
  ) {
    this.poetsdienst = poetsdienst?.hasContent() ? poetsdienst : undefined;
    this.gezinszorg = gezinszorg?.hasContent() ? gezinszorg : undefined;
    this.maaltijdenAanHuis = maaltijdenAanHuis?.hasContent() ? maaltijdenAanHuis : undefined;
    this.klusjesdienst = klusjesdienst?.hasContent() ? klusjesdienst : undefined;
    this.psycholoog = psycholoog?.hasContent() ? psycholoog : undefined;
    this.ergotherapeut = ergotherapeut?.hasContent() ? ergotherapeut : undefined;
    this.mantelzorgcoordinator = mantelzorgcoordinator?.hasContent() ? mantelzorgcoordinator : undefined;
    this.casemanager = casemanager?.hasContent() ? casemanager : undefined;
    this.casaClementina = casaClementina?.hasContent() ? casaClementina : undefined;
  }

  getRedenOpstart(): string | undefined {
    return this.redenOpstart;
  }

  getTeBehalenDoelstellingen(): string | undefined {
    return this.teBehalenDoelstellingen;
  }

  getPoetsdienst(): TakenpakketCategorie | undefined {
    return this.poetsdienst;
  }

  getGezinszorg(): TakenpakketCategorie | undefined {
    return this.gezinszorg;
  }

  getMaaltijdenAanHuis(): TakenpakketCategorie | undefined {
    return this.maaltijdenAanHuis;
  }

  getKlusjesdienst(): TakenpakketCategorie | undefined {
    return this.klusjesdienst;
  }

  getPsycholoog(): TakenpakketCategorie | undefined {
    return this.psycholoog;
  }

  getErgotherapeut(): TakenpakketCategorie | undefined {
    return this.ergotherapeut;
  }

  getMantelzorgcoordinator(): TakenpakketCategorie | undefined {
    return this.mantelzorgcoordinator;
  }

  getCasemanager(): TakenpakketCategorie | undefined {
    return this.casemanager;
  }

  getCasaClementina(): CasaClementina | undefined {
    return this.casaClementina;
  }

  getStofzuigerWaarTeVinden(): string | undefined {
    return this.stofzuigerWaarTeVinden;
  }

  getPoetsproductenWaarTeVinden(): string | undefined {
    return this.poetsproductenWaarTeVinden;
  }

  getDoekenVoddenDweilenWaarTeVinden(): string | undefined {
    return this.doekenVoddenDweilenWaarTeVinden;
  }

  getTelescopischeSteelWaarTeVinden(): string | undefined {
    return this.telescopischeSteelWaarTeVinden;
  }

  getAndereWaarTeVinden(): string | undefined {
    return this.andereWaarTeVinden;
  }

  hasWaarTeVindenInformation(): boolean {
    return !!this.stofzuigerWaarTeVinden
      || !!this.poetsproductenWaarTeVinden
      || !!this.doekenVoddenDweilenWaarTeVinden
      || !!this.telescopischeSteelWaarTeVinden
      || !!this.andereWaarTeVinden
  }

  getAandachtspuntenPoetsen(): string | undefined {
    return this.aandachtspuntenPoetsen;
  }

  getAandachtspuntenAndereTaken(): string | undefined {
    return this.aandachtspuntenAndereTaken;
  }

  hasContent(): boolean {
    return !!this.redenOpstart
      || !!this.teBehalenDoelstellingen
      || !!this.poetsdienst
      || !!this.gezinszorg
      || !!this.maaltijdenAanHuis
      || !!this.klusjesdienst
      || !!this.psycholoog
      || !!this.ergotherapeut
      || !!this.mantelzorgcoordinator
      || !!this.casemanager
      || !!this.casaClementina
      || !!this.stofzuigerWaarTeVinden
      || !!this.poetsproductenWaarTeVinden
      || !!this.doekenVoddenDweilenWaarTeVinden
      || !!this.telescopischeSteelWaarTeVinden
      || !!this.andereWaarTeVinden
      || !!this.aandachtspuntenPoetsen
      || !!this.aandachtspuntenAndereTaken;
  }
}

export class TakenpakketCategorie {

  static deserialize(dto: TakenPakketCategorieDto): TakenpakketCategorie {
    return new TakenpakketCategorie(
      dto.status,
      dto.frequentie,
      dto.vasteMedewerkers,
      dto.belangrijksteTaken,
      dto.specifiekeInformatieVoorWeekend,
    );
  }

  constructor(
    private status?: TakenpakketCategorieStatus,
    private frequentie?: string,
    private vasteMedewerkers?: string,
    private belangrijksteTaken?: string,
    private specifiekeInformatieVoorWeekend?: string,
  ) {
  }

  getStatus(): TakenpakketCategorieStatus | undefined {
    return this.status;
  }

  getFrequentie(): string | undefined {
    return this.frequentie;
  }

  getVasteMedewerkers(): string | undefined {
    return this.vasteMedewerkers;
  }

  getBelangrijksteTaken(): string | undefined {
    return this.belangrijksteTaken;
  }

  getSpecifiekeInformatieVoorWeekend(): string | undefined {
    return this.specifiekeInformatieVoorWeekend;
  }

  hasContent(): boolean {
    return !!this.status
      || !!this.frequentie
      || !!this.vasteMedewerkers
      || !!this.belangrijksteTaken
      || !!this.specifiekeInformatieVoorWeekend
  }
}

export enum TakenpakketCategorieStatus {
  OPGESTART = 'Opgestart',
  AANGEVRAAGD_MAAR_NOG_NIET_OPGESTART = 'Aangevraagd maar nog niet opgestart',
  NIET_NODIG = 'Niet nodig',
}

export class CasaClementina {
  static deserialize(dto: CasaClementinaDto): CasaClementina {
    return new CasaClementina(
      dto.financieleOpties,
      dto.transportOptiesNaarCasaClementina,
      dto.verduidelijkingAndereTransportOptieNaarCasaClementina,
      dto.contactpersoonNoodgeval ? ContactpersoonNoodgeval.deserialize(dto.contactpersoonNoodgeval) : undefined,
      dto.aandachtspuntenEnGemaakteAfspraken,
    );
  }

  constructor(
    private financieleOpties: Array<FinancieleOptie>,
    private transportOptiesNaarCasaClementina: Array<TransportOptieNaarCasaClementina>,
    private verduidelijkingAndereTransportOptieNaarCasaClementina?: string,
    private contactpersoonNoodgeval?: ContactpersoonNoodgeval,
    private aandachtspuntenEnGemaakteAfspraken?: string,
  ) {
    this.financieleOpties = financieleOpties || []
    this.transportOptiesNaarCasaClementina = transportOptiesNaarCasaClementina || []
    this.contactpersoonNoodgeval = contactpersoonNoodgeval?.hasContent() ? contactpersoonNoodgeval : undefined;
  }

  getFinancieleOpties(): Array<FinancieleOptie> {
    return this.financieleOpties;
  }

  getTransportOptiesNaarCasaClementina(): Array<TransportOptieNaarCasaClementina> {
    return this.transportOptiesNaarCasaClementina;
  }

  getVerduidelijkingAndereTransportOptieNaarCasaClementina(): string | undefined {
    return this.verduidelijkingAndereTransportOptieNaarCasaClementina;
  }

  getContactpersoonNoodgeval(): ContactpersoonNoodgeval | undefined {
    return this.contactpersoonNoodgeval;
  }

  getAandachtspuntenEnGemaakteAfspraken(): string | undefined {
    return this.aandachtspuntenEnGemaakteAfspraken;
  }

  hasContent(): boolean {
    return this.financieleOpties.length > 0
      || this.transportOptiesNaarCasaClementina.length > 0
      || !!this.verduidelijkingAndereTransportOptieNaarCasaClementina
      || !!this.contactpersoonNoodgeval
      || !!this.aandachtspuntenEnGemaakteAfspraken
  }

}

export enum TransportOptieNaarCasaClementina {
  ZELFSTANDIG = 'Cliënt komt zelfstandig',
  OPENBAAR_VERVOER_ZELFSTANDIG = 'Cliënt komt alleen met openbaar vervoer',
  OPENBAAR_VERVOER_MET_ONDERSTEUNING = 'Cliënt kan enkel met ondersteuning met openbaar vervoer komen',
  ENKEL_MET_MEDEWERKER_ZORG_LEUVEN = 'Cliënt geraakt er enkel met een medewerker van Zorg Leuven',
  OPGEHAALD_DOOR_BUSJE = 'Cliënt moet opgehaald worden door het busje',
  GERAAKT_ER_NIET = 'Cliënt geraakt niet in Casa Clementina',
  ANDERE = 'Andere',
}

export enum FinancieleOptie {
  BEWINDVOERDER = 'Cliënt heeft een bewindvoerder',
  VIA_FACTUUR = 'Alles wordt betaald via factuur',
  CLIENT_MET_BANKKAART = 'Cliënt kan zelf betalen met de bankkaart',
}

export class ContactpersoonNoodgeval {
  static deserialize(dto: ContactpersoonNoodgevalDto): ContactpersoonNoodgeval {
    return new ContactpersoonNoodgeval(
      dto.naam,
      dto.relatieTotHulpvrager,
      dto.telefoonnummer,
    );
  }

  constructor(
    private naam?: string,
    private relatieTotHulpvrager?: string,
    private telefoonnummer?: string,
  ) {
  }

  getNaam(): string | undefined {
    return this.naam
  }

  getRelatieTotHulpvrager(): string | undefined {
    return this.relatieTotHulpvrager
  }

  getTelefoonnummer(): string | undefined {
    return this.telefoonnummer
  }

  hasContent(): boolean {
    return !!this.naam
      || !!this.relatieTotHulpvrager
      || !!this.telefoonnummer
  }
}

export class Knelpunten {
  static deserialize(dto: KnelpuntenDto): Knelpunten {
    return new Knelpunten(
      dto.allergieen,
      dto.huisdieren,
      dto.roken,
    );
  }

  constructor(
    private allergieen?: string,
    private huisdieren?: string,
    private roken?: string,
  ) {
  }

  getAllergieen(): string | undefined {
    return this.allergieen;
  }

  getHuisdieren(): string | undefined {
    return this.huisdieren;
  }

  getRoken(): string | undefined {
    return this.roken;
  }

  hasContent(): boolean {
    return !!this.allergieen
      || !!this.huisdieren
      || !!this.roken;
  }
}

export class MogelijkeProblematieken {
  static deserialize(dto: MogelijkeProblematiekenDto): MogelijkeProblematieken {
    return new MogelijkeProblematieken(
      dto.fysiek,
      dto.mentaal,
      dto.financieel,
      dto.andere,
      dto.handvatenOmMetClientOmTeGaan,
      dto.afsprakennota,
    );
  }

  constructor(
    private fysiek?: string,
    private mentaal?: string,
    private financieel?: string,
    private andere?: string,
    private handvatenOmMetClientOmTeGaan?: string,
    private afsprakennota?: string,
  ) {
  }

  getFysiek(): string | undefined {
    return this.fysiek;
  }

  getMentaal(): string | undefined {
    return this.mentaal;
  }

  getFinancieel(): string | undefined {
    return this.financieel;
  }

  getAndere(): string | undefined {
    return this.andere;
  }

  getHandvatenOmMetClientOmTeGaan(): string | undefined {
    return this.handvatenOmMetClientOmTeGaan;
  }

  getAfsprakennota(): string | undefined {
    return this.afsprakennota;
  }

  hasContent(): boolean {
    return !!this.fysiek
      || !!this.mentaal
      || !!this.financieel
      || !!this.andere
      || !!this.handvatenOmMetClientOmTeGaan
      || !!this.afsprakennota;
  }
}

export interface ClientFicheDto {
  andereDiensten: Array<ClientFicheDienstDto>;
  laatstGewijzigdeTijdstip: string;
  algemeneInformatie?: AlgemeneInformatieDto;
  takenpakket?: TakenpakketDto;
  knelpunten?: KnelpuntenDto;
  mogelijkeProblematieken?: MogelijkeProblematiekenDto;
}

export interface ClientFicheDienstDto {
  functie?: string;
  naamDienstverlenerOfOrganisatie?: string;
  extraInfo?: string;
}

export interface AlgemeneInformatieDto {
  dienstencheques?: DienstenChequesType;
  gebruikersnummer?: number;
  geboortedatum?: string;
  moedertaal?: string;
  conversatieTaal?: string;
  keyLock?: string;
  autoStaanPlaats?: string;
  extraInfoBereikbaarheid?: string;
  inwonendeGezinsleden?: string;
  socialeSituatieSchets?: string;
  extraInformatie?: string;
}

export interface TakenpakketDto {
  redenOpstart?: string;
  teBehalenDoelstellingen?: string;
  poetsdienst?: TakenPakketCategorieDto;
  gezinszorg?: TakenPakketCategorieDto;
  maaltijdenAanHuis?: TakenPakketCategorieDto;
  klusjesdienst?: TakenPakketCategorieDto;
  psycholoog?: TakenPakketCategorieDto;
  ergotherapeut?: TakenPakketCategorieDto;
  mantelzorgcoordinator?: TakenPakketCategorieDto;
  casemanager?: TakenPakketCategorieDto;
  casaClementina?: CasaClementinaDto;
  stofzuigerWaarTeVinden?: string;
  poetsproductenWaarTeVinden?: string;
  doekenVoddenDweilenWaarTeVinden?: string;
  telescopischeSteelWaarTeVinden?: string;
  andereWaarTeVinden?: string;
  aandachtspuntenPoetsen?: string;
  aandachtspuntenAndereTaken?: string;
}

export interface TakenPakketCategorieDto {
  status?: TakenpakketCategorieStatus;
  frequentie?: string;
  vasteMedewerkers?: string;
  belangrijksteTaken?: string;
  specifiekeInformatieVoorWeekend?: string;
}

export interface CasaClementinaDto {
  contactpersoonNoodgeval?: ContactpersoonNoodgevalDto;
  aandachtspuntenEnGemaakteAfspraken?: string;
  transportOptiesNaarCasaClementina: Array<TransportOptieNaarCasaClementina>;
  verduidelijkingAndereTransportOptieNaarCasaClementina?: string;
  financieleOpties: Array<FinancieleOptie>;
}

export interface ContactpersoonNoodgevalDto {
  naam?: string;
  relatieTotHulpvrager?: string;
  telefoonnummer?: string;
}

export interface KnelpuntenDto {
  allergieen?: string;
  huisdieren?: string;
  roken?: string;
}

export interface MogelijkeProblematiekenDto {
  fysiek?: string;
  mentaal?: string;
  financieel?: string;
  andere?: string;
  handvatenOmMetClientOmTeGaan?: string;
  afsprakennota?: string;
}
